export class ApiError extends Error {
    status;
    code;
    fields;
    body;
    constructor(status, code, message, fields = {}, 
    /** The whole error response (some carry more, e.g. a flow's problems). */
    body = undefined) {
        super(message);
        this.status = status;
        this.code = code;
        this.fields = fields;
        this.body = body;
    }
}
/** "?a=1&b=2" from the set values, or "". */
function queryString(values) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(values))
        if (value !== undefined && value !== null && value !== '')
            params.set(key, String(value));
    const text = params.toString();
    return text ? `?${text}` : '';
}
export class ApiClient {
    baseUrl;
    fetchImpl;
    constructor(
    /** Origin of the site, e.g. https://pbx.example.com. Empty for same-origin. */
    baseUrl = '', fetchImpl = (...args) => fetch(...args)) {
        this.baseUrl = baseUrl;
        this.fetchImpl = fetchImpl;
    }
    // ---- Site and sign-in ----
    context = () => this.request('GET', '/api/context');
    signIn = (input) => this.request('POST', '/api/auth/sign-in', input);
    verifyMfa = (input) => this.request('POST', '/api/auth/mfa', input);
    signOut = () => this.request('POST', '/api/auth/sign-out');
    me = () => this.request('GET', '/api/auth/me');
    /** The language the person sees the apps in, everywhere they sign in. */
    setLanguage = (language) => this.request('PUT', '/api/me/language', { language });
    setPassword = (input) => this.request('POST', '/api/auth/set-password', input);
    changePassword = (input) => this.request('POST', '/api/auth/password', input);
    // ---- Softphone ----
    softphoneCredentials = (input) => this.request('POST', '/api/softphone/credentials', input);
    // ---- Users ----
    listUsers = () => this.request('GET', '/api/users');
    createUser = (input) => this.request('POST', '/api/users', input);
    updateUser = (id, input) => this.request('PATCH', `/api/users/${id}`, input);
    passwordLink = (id) => this.request('POST', `/api/users/${id}/password-link`);
    resetMfa = (id) => this.request('POST', `/api/users/${id}/reset-mfa`);
    /** A person's photo. `userId` null: the signed-in person's own; else (admins) someone else's. */
    uploadPhoto = (file, userId = null) => this.request('PUT', `${this.personPath(userId)}/photo`, file);
    removePhoto = (userId = null) => this.request('DELETE', `${this.personPath(userId)}/photo`);
    photoUrl = (userId, version) => `${this.baseUrl}/api/users/${userId}/photo?v=${version}`;
    // ---- Regions ----
    listRegions = () => this.request('GET', '/api/regions');
    createRegion = (input) => this.request('POST', '/api/regions', input);
    updateRegion = (id, input) => this.request('PUT', `/api/regions/${id}`, input);
    deleteRegion = (id) => this.request('DELETE', `/api/regions/${id}`);
    // ---- Branding ----
    branding = () => this.request('GET', '/api/branding');
    updateBranding = (input) => this.request('PUT', '/api/branding', input);
    uploadBrandingImage = (kind, file) => this.request('PUT', `/api/branding/${kind}`, file);
    deleteBrandingImage = (kind) => this.request('DELETE', `/api/branding/${kind}`);
    // ---- Calls ----
    listCalls = (query = {}) => this.request('GET', `/api/calls${queryString(query)}`);
    getCall = (id) => this.request('GET', `/api/calls/${id}`);
    /** Address of a call's recording, for an <audio> element (same origin, uses the session cookie). */
    callRecordingUrl = (id, download = false) => `${this.baseUrl}/api/calls/${id}/recording${download ? '?download=1' : ''}`;
    /** Stops this person's browsers and apps ringing for the call, so it moves on (mobile, voicemail). */
    declineCall = (id) => this.request('POST', `/api/calls/${id}/decline`);
    activity = (query = {}) => this.request('GET', `/api/stats/activity${queryString(query)}`);
    // ---- AI call analysis ----
    correctOutcome = (callId, outcome) => this.request('PUT', `/api/calls/${callId}/review/outcome`, { outcome });
    correctReasons = (callId, categoryIds) => this.request('PUT', `/api/calls/${callId}/review/reasons`, { categoryIds });
    /** Transcribes the call again with the current settings (e.g. another model); its review follows. */
    transcribeAgain = (callId) => this.request('POST', `/api/calls/${callId}/transcript/rerun`);
    /** Corrects one transcript line; it's also kept to fine-tune the model on. */
    correctTranscriptLine = (callId, lineId, text) => this.request('PUT', `/api/calls/${callId}/transcript/lines/${lineId}`, { text });
    /** Confirms every line of the transcript is right (after correcting what wasn't). */
    markTranscriptChecked = (callId) => this.request('POST', `/api/calls/${callId}/transcript/checked`);
    rerunReview = (callId) => this.request('POST', `/api/calls/${callId}/review/rerun`);
    markFindingWrong = (findingId, note) => this.request('POST', `/api/findings/${findingId}/wrong`, { note });
    analysisFeedback = () => this.request('GET', '/api/analysis/feedback');
    analysisSettings = () => this.request('GET', '/api/settings/analysis');
    updateAnalysisSettings = (input) => this.request('PUT', '/api/settings/analysis', input);
    listCategories = () => this.request('GET', '/api/analysis/categories');
    createCategory = (input) => this.request('POST', '/api/analysis/categories', input);
    updateCategory = (id, input) => this.request('PUT', `/api/analysis/categories/${id}`, input);
    insightReasons = (query = {}) => this.request('GET', `/api/insights/reasons${queryString(query)}`);
    insightPeople = (query = {}) => this.request('GET', `/api/insights/people${queryString(query)}`);
    listDigests = () => this.request('GET', '/api/digests');
    getDigest = (id) => this.request('GET', `/api/digests/${id}`);
    // ---- Recording notice, pausing a recording, the audit log ----
    recordingNotice = () => this.request('GET', '/api/settings/recording-notice');
    updateRecordingNoticeText = (text) => this.request('PUT', '/api/settings/recording-notice', { text });
    uploadAnnouncement = (language, file) => this.request('PUT', `/api/announcements/${language}`, file);
    deleteAnnouncement = (language) => this.request('DELETE', `/api/announcements/${language}`);
    announcementAudioUrl = (language) => `${this.baseUrl}/api/announcements/${language}/audio`;
    // ---- When I can't answer: calls for a person's own number, their voicemail greetings ----
    // `userId` null: the signed-in person; else someone an admin or region manager can see.
    callRules = (userId = null) => this.request('GET', `${this.personPath(userId)}/call-rules`);
    updateCallRules = (rules, userId = null) => this.request('PUT', `${this.personPath(userId)}/call-rules`, rules);
    uploadGreeting = (language, file, userId = null) => this.request('PUT', `${this.personPath(userId)}/greetings/${language}`, file);
    deleteGreeting = (language, userId = null) => this.request('DELETE', `${this.personPath(userId)}/greetings/${language}`);
    greetingAudioUrl = (language, userId = null) => `${this.baseUrl}${this.personPath(userId)}/greetings/${language}/audio`;
    personPath = (userId) => (userId ? `/api/users/${userId}` : '/api/me');
    /** The signed-in person's recorded call in progress, if any. */
    activeCall = () => this.request('GET', '/api/calls/active');
    setRecordingPaused = (paused) => this.request('POST', '/api/calls/active/recording', { paused });
    audit = (query = {}) => this.request('GET', `/api/audit${queryString(query)}`);
    /** Before a table is downloaded as Excel: noted in the audit log, refused below managers. */
    tableExported = (input) => this.request('POST', '/api/audit/table-exported', input);
    /** Rings the person's mobile, then connects it to the number. */
    clickToCall = (input) => this.request('POST', '/api/calls/click-to-call', input);
    // ---- Voicemail ----
    listVoicemails = () => this.request('GET', '/api/voicemails');
    /** Address of a message's audio, for an <audio> element (same origin, uses the session cookie). */
    voicemailAudioUrl = (id) => `${this.baseUrl}/api/voicemails/${id}/audio`;
    // ---- Texts ----
    /** Conversations this person sees, latest first; with `remote`, only those with that number. */
    listTexts = (query = {}) => this.request('GET', `/api/texts${queryString(query)}`);
    getText = (id) => this.request('GET', `/api/texts/${id}`);
    /** Opened: its texts count as read, for everyone. */
    markTextRead = (id) => this.request('POST', `/api/texts/${id}/read`);
    replyText = (id, input) => this.request('POST', `/api/texts/${id}/messages`, input);
    /** A text to a number: a new conversation, or the one there is with them. */
    startText = (input) => this.request('POST', '/api/texts', input);
    /** Our numbers this person may text from. */
    textNumbers = () => this.request('GET', '/api/texts/numbers');
    /** Conversations with unread texts. */
    textUnread = () => this.request('GET', '/api/texts/unread');
    // ---- Notifications ----
    listNotifications = () => this.request('GET', '/api/notifications');
    markNotificationsRead = (ids) => this.request('POST', '/api/notifications/read', ids === 'all' ? { all: true } : { ids });
    /** Server-sent events: "notification" and "unread". */
    notificationStreamUrl = () => `${this.baseUrl}/api/notifications/stream`;
    notificationPreferences = () => this.request('GET', '/api/notifications/preferences');
    updateNotificationPreferences = (input) => this.request('PUT', '/api/notifications/preferences', input);
    notificationSettings = () => this.request('GET', '/api/settings/notifications');
    updateNotificationSettings = (input) => this.request('PUT', '/api/settings/notifications', input);
    /** This browser's push subscription, for notifications while the app is closed. */
    savePushSubscription = (subscription) => this.request('PUT', '/api/softphone/push', subscription);
    removePushSubscription = () => this.request('DELETE', '/api/softphone/push');
    /** A phone app's push token from Apple or Google. */
    savePhonePushToken = (input) => this.request('PUT', '/api/softphone/phone-push', input);
    removePhonePushToken = () => this.request('DELETE', '/api/softphone/phone-push');
    /** A call this phone was woken for: still ringing, or answered or over (the app then takes it off the call screen). */
    pushedCallState = (id) => this.request('GET', `/api/softphone/calls/${id}`);
    // ---- Carriers, numbers and call settings ----
    listTrunks = () => this.request('GET', '/api/trunks');
    createTrunk = (input) => this.request('POST', '/api/trunks', input);
    updateTrunk = (id, input) => this.request('PUT', `/api/trunks/${id}`, input);
    deleteTrunk = (id) => this.request('DELETE', `/api/trunks/${id}`);
    // ---- Queues (M9) ----
    listQueues = () => this.request('GET', '/api/queues');
    createQueue = (input) => this.request('POST', '/api/queues', input);
    updateQueue = (id, input) => this.request('PUT', `/api/queues/${id}`, input);
    deleteQueue = (id) => this.request('DELETE', `/api/queues/${id}`);
    /** The signed-in person's status and their queues. */
    myQueues = () => this.request('GET', '/api/me/queues');
    setAgentStatus = (status) => this.request('PUT', '/api/me/status', { status });
    setQueueJoined = (queueId, joined) => this.request('PUT', `/api/me/queues/${queueId}`, { joined });
    /** The queues this person may watch, now; then live "queue" events on the event stream. */
    liveQueues = () => this.request('GET', '/api/queues/live');
    listCallbacks = (query = {}) => this.request('GET', `/api/callbacks${queryString(query)}`);
    setCallbackDone = (id, done) => this.request('PUT', `/api/callbacks/${id}`, { done });
    queueReport = (query) => this.request('GET', `/api/stats/queues${queryString(query)}`);
    // ---- Call flows (M10) ----
    listFlows = () => this.request('GET', '/api/flows');
    getFlow = (id) => this.request('GET', `/api/flows/${id}`);
    createFlow = (input) => this.request('POST', '/api/flows', input);
    /** Saves the draft; calls keep the published version until it is published. */
    saveFlow = (id, input) => this.request('PUT', `/api/flows/${id}`, input);
    deleteFlow = (id) => this.request('DELETE', `/api/flows/${id}`);
    validateFlow = (id, graph) => this.request('POST', `/api/flows/${id}/validate`, { graph });
    publishFlow = (id) => this.request('POST', `/api/flows/${id}/publish`);
    restoreFlowVersion = (id, versionId) => this.request('POST', `/api/flows/${id}/versions/${versionId}/restore`);
    flowVersion = (id, versionId) => this.request('GET', `/api/flows/${id}/versions/${versionId}`);
    setFlowNumbers = (id, numberIds) => this.request('PUT', `/api/flows/${id}/numbers`, { numberIds });
    simulateFlow = (id, input) => this.request('POST', `/api/flows/${id}/simulate`, input);
    flowCallPath = (callId) => this.request('GET', `/api/flows/calls/${callId}/path`);
    flowStats = (id, days = 7) => this.request('GET', `/api/flows/${id}/stats?days=${days}`);
    flowTemplates = () => this.request('GET', '/api/flows/templates');
    flowResources = (regionId, flowId) => this.request('GET', `/api/flows/resources${queryString({ regionId, flowId })}`);
    // ---- Contacts ----
    listContacts = (q = '', limit) => this.request('GET', `/api/contacts${queryString({ q, ...(limit && { limit }) })}`);
    getContact = (id) => this.request('GET', `/api/contacts/${id}`);
    createContact = (input) => this.request('POST', '/api/contacts', input);
    updateContact = (id, input) => this.request('PUT', `/api/contacts/${id}`, input);
    deleteContact = (id) => this.request('DELETE', `/api/contacts/${id}`);
    /** Who is on a call (ringing or in progress), and the latest calls with them. */
    callerInfo = (callId) => this.request('GET', `/api/calls/${callId}/caller`);
    /** The same for a number (calls out). */
    numberInfo = (number) => this.request('GET', `/api/contacts/by-number${queryString({ number })}`);
    // ---- Pipeline ----
    pipeline = (filter = {}) => this.request('GET', `/api/pipeline${queryString({ ...filter, due: filter.due ? '1' : '' })}`);
    /** More of a column's leads, after the first `skip`. */
    listLeads = (stage, skip, filter = {}) => this.request('GET', `/api/pipeline/leads${queryString({ ...filter, due: filter.due ? '1' : '', stage, skip: String(skip) })}`);
    getLead = (id) => this.request('GET', `/api/pipeline/leads/${id}`);
    createLead = (input) => this.request('POST', '/api/pipeline/leads', input);
    updateLead = (id, input) => this.request('PUT', `/api/pipeline/leads/${id}`, input);
    moveLead = (id, move) => this.request('PATCH', `/api/pipeline/leads/${id}`, move);
    deleteLead = (id) => this.request('DELETE', `/api/pipeline/leads/${id}`);
    /** The leads the board shows (as filtered), as an Excel file (managers and admins), with headings in `language`. */
    exportLeadsUrl = (filter, language) => `${this.baseUrl}/api/pipeline/export${queryString({ ...filter, due: filter.due ? '1' : '', language })}`;
    listHoldMusic = () => this.request('GET', '/api/hold-music');
    createHoldMusic = (input) => this.request('POST', '/api/hold-music', input);
    updateHoldMusic = (id, input) => this.request('PUT', `/api/hold-music/${id}`, input);
    deleteHoldMusic = (id) => this.request('DELETE', `/api/hold-music/${id}`);
    addHoldMusicTrack = (id, file) => this.request('POST', `/api/hold-music/${id}/tracks?name=${encodeURIComponent(file.name.slice(0, 120))}`, file);
    deleteHoldMusicTrack = (id, trackId) => this.request('DELETE', `/api/hold-music/${id}/tracks/${trackId}`);
    holdMusicAudioUrl = (id, trackId) => `${this.baseUrl}/api/hold-music/${id}/tracks/${trackId}/audio`;
    listPrompts = () => this.request('GET', '/api/prompts');
    createPrompt = (input) => this.request('POST', '/api/prompts', input);
    updatePrompt = (id, input) => this.request('PUT', `/api/prompts/${id}`, input);
    deletePrompt = (id) => this.request('DELETE', `/api/prompts/${id}`);
    /** A new version in a language: an audio file, or a recording made in the browser. */
    addPromptRecording = (id, language, audio) => this.request('POST', `/api/prompts/${id}/recordings/${language}`, audio);
    deletePromptRecording = (id, recordingId) => this.request('DELETE', `/api/prompts/${id}/recordings/${recordingId}`);
    promptAudioUrl = (id, recordingId, original = false) => `${this.baseUrl}/api/prompts/${id}/recordings/${recordingId}/audio${original ? '?original=1' : ''}`;
    listOpeningHours = () => this.request('GET', '/api/opening-hours');
    createOpeningHours = (input) => this.request('POST', '/api/opening-hours', input);
    updateOpeningHours = (id, input) => this.request('PUT', `/api/opening-hours/${id}`, input);
    deleteOpeningHours = (id) => this.request('DELETE', `/api/opening-hours/${id}`);
    listCallerLists = () => this.request('GET', '/api/caller-lists');
    createCallerList = (input) => this.request('POST', '/api/caller-lists', input);
    updateCallerList = (id, input) => this.request('PUT', `/api/caller-lists/${id}`, input);
    deleteCallerList = (id) => this.request('DELETE', `/api/caller-lists/${id}`);
    listNumbers = () => this.request('GET', '/api/numbers');
    createNumber = (input) => this.request('POST', '/api/numbers', input);
    updateNumber = (id, input) => this.request('PUT', `/api/numbers/${id}`, input);
    deleteNumber = (id) => this.request('DELETE', `/api/numbers/${id}`);
    transcriptionSettings = () => this.request('GET', '/api/settings/transcription');
    updateTranscriptionSettings = (input) => this.request('PUT', '/api/settings/transcription', input);
    crmSettings = () => this.request('GET', '/api/settings/crm');
    connectCrm = (input) => this.request('PUT', '/api/settings/crm/connection', input);
    disconnectCrm = () => this.request('DELETE', '/api/settings/crm/connection');
    updateCrmSettings = (input) => this.request('PUT', '/api/settings/crm', input);
    /** SmartOn asked for this call: it goes into that sales event. Before dialling. */
    crmCallIntent = (input) => this.request('POST', '/api/calls/crm-intent', input);
    callSettings = () => this.request('GET', '/api/settings/calls');
    updateCallSettings = (input) => this.request('PUT', '/api/settings/calls', input);
    // ---- Platform console ----
    platform = {
        signIn: (input) => this.request('POST', '/api/platform/auth/sign-in', input),
        verifyMfa: (input) => this.request('POST', '/api/platform/auth/mfa', input),
        signOut: () => this.request('POST', '/api/platform/auth/sign-out'),
        me: () => this.request('GET', '/api/platform/auth/me'),
        setPassword: (input) => this.request('POST', '/api/platform/auth/set-password', input),
        changePassword: (input) => this.request('POST', '/api/platform/auth/password', input),
        listTenants: () => this.request('GET', '/api/platform/tenants'),
        createTenant: (input) => this.request('POST', '/api/platform/tenants', input),
        tenant: (id) => this.request('GET', `/api/platform/tenants/${id}`),
        updateTenant: (id, input) => this.request('PATCH', `/api/platform/tenants/${id}`, input),
        suspendTenant: (id) => this.request('POST', `/api/platform/tenants/${id}/suspend`),
        activateTenant: (id) => this.request('POST', `/api/platform/tenants/${id}/activate`),
        createTenantAdmin: (id, input) => this.request('POST', `/api/platform/tenants/${id}/admins`, input),
        uploadTenantImage: (id, kind, file) => this.request('PUT', `/api/platform/tenants/${id}/${kind}`, file),
        tenantUsage: (id) => this.request('GET', `/api/platform/tenants/${id}/usage`),
        tenantAudit: (id) => this.request('GET', `/api/platform/tenants/${id}/audit`),
        /** The default logo and favicon, shown for tenants without their own. */
        defaultBranding: () => this.request('GET', '/api/platform/default-branding'),
        uploadDefaultImage: (kind, file) => this.request('PUT', `/api/platform/default-branding/${kind}`, file),
        removeDefaultImage: (kind) => this.request('DELETE', `/api/platform/default-branding/${kind}`),
        defaultImageUrl: (kind, version) => `${this.baseUrl}/api/platform/default-branding/${kind}?v=${version}`,
    };
    async request(method, path, body) {
        const headers = { Accept: 'application/json' };
        let payload;
        if (body instanceof Blob) {
            headers['Content-Type'] = body.type || 'application/octet-stream';
            payload = body;
        }
        else if (body !== undefined) {
            headers['Content-Type'] = 'application/json';
            payload = JSON.stringify(body);
        }
        const response = await this.fetchImpl(`${this.baseUrl}${path}`, {
            method,
            headers,
            body: payload ?? null,
            credentials: 'include',
        });
        if (response.status === 204)
            return undefined;
        const text = await response.text();
        const data = text ? JSON.parse(text) : undefined;
        if (!response.ok) {
            const error = (data ?? {});
            throw new ApiError(response.status, error.error ?? 'error', error.message ?? response.statusText, error.fields, data);
        }
        return data;
    }
}
